import client from './client';
import {
  PayrollEditEntryKind,
  PayrollEntryBody,
  PayrollEntryResult,
  PayrollSubmitTimesheetRequest,
  PayrollSubmitTimesheetResponse,
  PayrollTimesheetState,
  PunchHistoryResponse,
  TimesheetChangeLogResponse,
  TimesheetPreview,
  TimesheetRevisionDetailResponse,
  TimesheetRevisionListResponse,
} from '../interfaces/TimesheetEditInterface';

/**
 * Payroll Timesheet Modifications — axios wrappers for the /admin/timesheetEdit
 * endpoints (Milestone 1, M1-B2). Response shapes are documented on the
 * interfaces in interfaces/TimesheetEditInterface.ts.
 *
 * Unlike the older api modules these rethrow the ORIGINAL axios error instead of
 * wrapping it in a fresh Error: callers must be able to read
 * `err.response.status` — the submit's mid-flow-conflict 409 and the state
 * read's permission 403 are both meaningful, designed states, not failures to
 * swallow.
 */

/**
 * GET /admin/timesheetEdit/state — what payroll may still edit / submit.
 * Returns the editable state (relaxed read: inactive employees and punch-less
 * weeks included, full day skeleton), or `{ exists: true, timesheetID }` when
 * the period was already submitted. 404 = the employee does not exist.
 */
export const getPayrollTimesheetState = async (
  employeeID: number,
  payPeriodID: number
): Promise<PayrollTimesheetState> => {
  try {
    const response = await client.get('/admin/timesheetEdit/state', {
      params: { employeeID, payPeriodID },
    });
    return response.data as PayrollTimesheetState;
  } catch (error) {
    console.error('Error fetching payroll timesheet state:', error);
    throw error;
  }
};

/**
 * POST /admin/timesheetEdit/submit — submit a timesheet on the employee's
 * behalf (recorded as a PAYROLL submission; the server stamps signature type,
 * acting admin, and tenant-zone submitted date). Throws the raw axios error on
 * 409 (employee submitted mid-flow) and 400 (stale ID arrays) — the M1-B3 modal
 * owns recovery for both.
 */
export const submitTimesheetForEmployee = async (
  request: PayrollSubmitTimesheetRequest
): Promise<PayrollSubmitTimesheetResponse> => {
  try {
    const response = await client.post('/admin/timesheetEdit/submit', request);
    return response.data as PayrollSubmitTimesheetResponse;
  } catch (error) {
    console.error('Error submitting timesheet for employee:', error);
    throw error;
  }
};

/**
 * GET /admin/timesheetEdit/preview — pre-submission running totals: the server's
 * real hour calculation (same arm the submit runs; WCDP = legacy CAWeekCalc,
 * weekly-40h OT) with the two INSERTs left behind. Read-only by construction —
 * writes nothing, safe to call repeatedly. Already-submitted periods come back
 * as `{ exists: true, timesheetID }` (a 200, mirroring `/state`), never an error.
 */
export const getPayrollTimesheetPreview = async (
  employeeID: number,
  payPeriodID: number
): Promise<TimesheetPreview> => {
  try {
    const response = await client.get('/admin/timesheetEdit/preview', {
      params: { employeeID, payPeriodID },
    });
    return response.data as TimesheetPreview;
  } catch (error) {
    console.error('Error fetching payroll timesheet preview:', error);
    throw error;
  }
};

/** GET /admin/timesheetEdit/changeLog — every field-level change, chronological. */
export const getTimesheetChangeLog = async (
  employeeID: number,
  payPeriodID: number
): Promise<TimesheetChangeLogResponse> => {
  try {
    const response = await client.get('/admin/timesheetEdit/changeLog', {
      params: { employeeID, payPeriodID },
    });
    return response.data as TimesheetChangeLogResponse;
  } catch (error) {
    console.error('Error fetching timesheet change log:', error);
    throw error;
  }
};

/**
 * GET /admin/timesheetEdit/punchHistory — TAS-365: per-punch change history for
 * one employee + pay period (every TimeEntry / TimeEntryHourly punch with
 * history, deleted ones included). One call per console week.
 */
export const getTimesheetPunchHistory = async (
  employeeID: number,
  payPeriodID: number
): Promise<PunchHistoryResponse> => {
  try {
    const response = await client.get('/admin/timesheetEdit/punchHistory', {
      params: { employeeID, payPeriodID },
    });
    return response.data as PunchHistoryResponse;
  } catch (error) {
    console.error('Error fetching punch history:', error);
    throw error;
  }
};

/** GET /admin/timesheetEdit/revisions — the revision list WITHOUT payloads. */
export const getTimesheetRevisions = async (
  employeeID: number,
  payPeriodID: number
): Promise<TimesheetRevisionListResponse> => {
  try {
    const response = await client.get('/admin/timesheetEdit/revisions', {
      params: { employeeID, payPeriodID },
    });
    return response.data as TimesheetRevisionListResponse;
  } catch (error) {
    console.error('Error fetching timesheet revisions:', error);
    throw error;
  }
};

// ── Milestone 2 — entry CRUD (§8: 21 routes over 7 kinds) ────────────────────
// Common semantics: editSessionID REQUIRED (GUID) on every call; one transaction
// per request; 409 (verbatim contractual message) when the period was submitted;
// date-range 400s name the field and both period bounds; the task kind 400s with
// "Insufficient remaining amount for this task." — all designed UI states, so
// these wrappers rethrow the ORIGINAL axios error (callers read response.status
// and response.data.message).

/** POST /admin/timesheetEdit/{kind} — create one entry. */
export const createPayrollEntry = async (
  kind: PayrollEditEntryKind,
  body: PayrollEntryBody
): Promise<PayrollEntryResult> => {
  try {
    const response = await client.post(`/admin/timesheetEdit/${kind}`, body);
    return response.data as PayrollEntryResult;
  } catch (error) {
    console.error(`Error creating payroll ${kind} entry:`, error);
    throw error;
  }
};

/** PUT /admin/timesheetEdit/{kind}/:id — update one entry (full body per kind). */
export const updatePayrollEntry = async (
  kind: PayrollEditEntryKind,
  entityID: number,
  body: PayrollEntryBody
): Promise<PayrollEntryResult> => {
  try {
    const response = await client.put(`/admin/timesheetEdit/${kind}/${entityID}`, body);
    return response.data as PayrollEntryResult;
  } catch (error) {
    console.error(`Error updating payroll ${kind} entry ${entityID}:`, error);
    throw error;
  }
};

/**
 * DELETE /admin/timesheetEdit/{kind}/:id. The editSessionID goes in BOTH the
 * body and the query string — the contract's fallback for clients/proxies that
 * strip DELETE payloads (§8.0).
 */
export const deletePayrollEntry = async (
  kind: PayrollEditEntryKind,
  entityID: number,
  editSessionID: string
): Promise<PayrollEntryResult> => {
  try {
    const response = await client.delete(`/admin/timesheetEdit/${kind}/${entityID}`, {
      data: { editSessionID },
      params: { editSessionID },
    });
    return response.data as PayrollEntryResult;
  } catch (error) {
    console.error(`Error deleting payroll ${kind} entry ${entityID}:`, error);
    throw error;
  }
};

/** GET /admin/timesheetEdit/revisions/:id — one revision with its full payload. */
export const getTimesheetRevisionByID = async (
  timesheetRevisionSnapshotID: number
): Promise<TimesheetRevisionDetailResponse> => {
  try {
    const response = await client.get(
      `/admin/timesheetEdit/revisions/${timesheetRevisionSnapshotID}`
    );
    return response.data as TimesheetRevisionDetailResponse;
  } catch (error) {
    console.error('Error fetching timesheet revision:', error);
    throw error;
  }
};
